import React, { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { Time } from "./time";

// "What the code contacts": what the latest analysed version of the item's npm / PyPI package does when it
// runs, from its package_contents snapshot. Domains are plain text (never links: they are indicators).
export default function CodeEvidencePanel({ itemId, styles }) {
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api.codeEvidence(itemId).then((r) => { setEv(r.data); setErr(""); }).catch((ex) => setErr(ex.message)), [itemId]);
  useEffect(() => { load(); }, [load]);
  const queue = async () => {
    try {
      const r = await api.queuePackageScan(itemId);
      setMsg(`Queued ${r.data.queued.join(", ") || "nothing"} for the next package-watch run.${r.data.note ? " " + r.data.note : ""}`);
      load();
    } catch (ex) { setMsg("Error: " + ex.message); }
  };
  if (err) return <div style={styles.card} className="hs-text-danger">Code evidence unavailable: {err}</div>;
  if (!ev) return <div style={styles.card}>Loading code evidence…</div>;
  const packages = ev.packages || [];
  const anyUnscanned = packages.some((p) => !p.scanned);
  const list = (xs) => (xs && xs.length ? xs.map((d) => <code key={d} style={{ display: "inline-block", marginRight: 8 }}>{d}</code>) : <span style={{ color: "var(--hs-text-muted)" }}>none</span>);
  return (
    <div style={styles.card} data-testid="code-evidence">
      <h3 style={{ marginTop: 0 }}>What the code contacts</h3>
      {!ev.has_packages && <div style={{ color: "var(--hs-text-muted)" }}>This server declares no npm or PyPI package, so there is no code to inspect.</div>}
      {packages.map((p) => (
        <div key={p.ref} style={{ borderTop: "1px solid var(--hs-border)", paddingTop: 8, marginTop: 8 }} data-testid={`code-evidence-${p.ref}`}>
          <div><code>{p.ref}</code>{p.scanned ? <> · version <strong>{p.version}</strong> · analysed <Time value={p.fetched_at} dateOnly /></> : null}</div>
          {!p.scanned ? (
            <div style={{ marginTop: 4 }} data-testid="code-evidence-not-scanned">
              <span className="hs-badge">Not scanned yet</span>
              {p.scan_requested_at && <span style={{ fontSize: 12, color: "var(--hs-text-muted)", marginLeft: 8 }}>queued <Time value={p.scan_requested_at} /></span>}
            </div>
          ) : (
            <dl style={{ display: "grid", gridTemplateColumns: "minmax(140px, max-content) 1fr", gap: "4px 12px", margin: "8px 0 0", fontSize: 14 }}>
              <dt style={{ color: "var(--hs-text-muted)" }}>First-party-looking domains</dt><dd style={{ margin: 0 }} data-testid="domains-first-party">{list(p.domains?.first_party)}</dd>
              <dt style={{ color: "var(--hs-text-muted)" }}>Other domains</dt><dd style={{ margin: 0 }} data-testid="domains-other">{list(p.domains?.other)}</dd>
              <dt style={{ color: "var(--hs-text-muted)" }}>Install scripts</dt>
              <dd style={{ margin: 0 }} data-testid="install-scripts">{Object.keys(p.install_scripts || {}).length
                ? Object.entries(p.install_scripts).map(([k, v]) => <div key={k}><code>{k}</code>: <code>{v}</code></div>)
                : <span style={{ color: "var(--hs-text-muted)" }}>none</span>}</dd>
              <dt style={{ color: "var(--hs-text-muted)" }}>Credential / sensitive-file reads</dt>
              <dd style={{ margin: 0 }} data-testid="credential-reads">{(p.credential_reads || []).length
                ? (p.credential_read_details || p.credential_reads.map((label) => ({ label }))).map((c) => c.safety_check
                  // Only found in guard / sandbox / deny-list code: most likely there to BLOCK these paths (weighted down by the rules).
                  ? <span key={c.label} className="hs-badge hs-badge-neutral" style={{ marginRight: 6 }} title={`only in ${c.file}`}
                          data-testid={`cred-safety-${c.label}`}>{c.label} · safety check</span>
                  : <span key={c.label} className="hs-badge hs-badge-danger" style={{ marginRight: 6 }} title={c.file ? `in ${c.file}` : undefined}
                          data-testid={`cred-read-${c.label}`}>{c.label}</span>)
                : <span style={{ color: "var(--hs-text-muted)" }}>none</span>}</dd>
            </dl>
          )}
        </div>))}
      {anyUnscanned && (
        <button type="button" data-testid="queue-package-scan" style={{ ...styles.btnDim, marginTop: 8 }} onClick={queue}>Queue a package scan</button>)}
      {msg && <div style={{ fontSize: 12, color: "var(--hs-text-muted)", marginTop: 6 }} data-testid="code-evidence-msg">{msg}</div>}
    </div>
  );
}
